// ============================================================================
// FSA DEPRECIATION CALCULATION SERVICE  (ADR-0006)
// ============================================================================

import { get, put } from '../../client/axiosClient';
import { fsaDepreciationCalcEndpoints } from '../../endpoints/fsa/fsaDepreciationCalc.endpoints';
import type { ApiResponse, ID } from '../../types';
import type {
  DepCalcWorkspace,
  DepDtSuggestion,
  DepNoteSuggestion,
  SaveDepCalcRequest,
} from '../../types/fsa/depreciationCalc.types';

class FsaDepreciationCalcService {
  /** Full workspace (get-or-create on the backend). */
  async getWorkspace(engagementId: ID): Promise<ApiResponse<DepCalcWorkspace>> {
    const r = await get<ApiResponse<DepCalcWorkspace>>(
      fsaDepreciationCalcEndpoints.workspace(engagementId),
    );
    return r.data;
  }

  /** Save the full workspace (header + registers, UI-computed values persisted verbatim). */
  async save(engagementId: ID, request: SaveDepCalcRequest): Promise<ApiResponse<DepCalcWorkspace>> {
    const r = await put<ApiResponse<DepCalcWorkspace>>(
      fsaDepreciationCalcEndpoints.save(engagementId),
      request,
    );
    return r.data;
  }

  /** Working-paper PDF as a Blob (blob-in-iframe / download pattern). */
  async getPdfBlob(engagementId: ID): Promise<Blob> {
    const r = await get<Blob>(fsaDepreciationCalcEndpoints.pdf(engagementId), { responseType: 'blob' });
    return r.data;
  }

  /** Working-paper Excel workbook as a Blob (download). */
  async getExcelBlob(engagementId: ID): Promise<Blob> {
    const r = await get<Blob>(fsaDepreciationCalcEndpoints.excel(engagementId), { responseType: 'blob' });
    return r.data;
  }

  /** Phase 1b — movement-column suggestions for a PPE / Intangibles schedule. */
  async getNoteSuggestions(engagementId: ID, scheduleId: ID): Promise<ApiResponse<DepNoteSuggestion>> {
    const r = await get<ApiResponse<DepNoteSuggestion>>(
      fsaDepreciationCalcEndpoints.suggestions(engagementId, scheduleId),
    );
    return r.data;
  }

  /** Phase 1b — deferred tax position + charge suggestion. */
  async getDtSuggestion(engagementId: ID): Promise<ApiResponse<DepDtSuggestion>> {
    const r = await get<ApiResponse<DepDtSuggestion>>(
      fsaDepreciationCalcEndpoints.dtSuggestion(engagementId),
    );
    return r.data;
  }
}

export const fsaDepreciationCalcService = new FsaDepreciationCalcService();
export default fsaDepreciationCalcService;
